import { useState, useEffect, useRef } from 'react'
import { useAuthFetch } from '../lib/useAuthFetch'
import { invalidateAlertCount } from './useUnreviewedAlertCount'
import { performBatchMarkReviewed, type BatchResult } from '../lib/batchReview'

const API = import.meta.env.VITE_API_URL ?? ''

export interface Alert {
  id: string
  alert_type: string
  competitor_id?: string | null
  competitor_name: string
  competitor_address?: string | null
  business_type?: string | null
  location_id?: string | null
  location_name?: string
  old_value: string | null
  new_value: string | null
  entity_id?: string | null
  entity_name?: string | null
  entity_brand?: string | null
  entity_category?: string | null
  entity_subcategory?: string | null
  entity_package_size?: string | null
  confidence: string
  reviewed: boolean
  reviewed_at?: string | null
  created_at: string
  // Detail view only (GET /alerts/:id): the operator's live relationship to this
  // rival at this location — 'block' > 'track'; null if not resolvable.
  slot_type?: string | null
  blocked_at?: string | null
  tracked_since?: string | null
}

export interface AlertGroup { id: string; alerts: Alert[] }

interface UseAlertsOptions {
  locationId?: string
  competitorId?: string  // filter to a single tracked rival (server-side)
  // 'unreviewed' (default) = only unreviewed, 'all' = all, 'reviewed' = reviewed only
  reviewed?: 'unreviewed' | 'all' | 'reviewed'
  limit?: number
  type?: string  // 'storefront' | 'delivery' — filter by competitor business_type
  surface?: 'today'  // recent, decision-worthy signals only
  brands?: string[]
  productTypes?: string[]
  sort?: string
  actionType?: string
  includeFacets?: boolean
  groupPrices?: boolean
  enabled?: boolean
}

export function useAlerts(options: UseAlertsOptions = {}) {
  const authFetch = useAuthFetch()
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [groups, setGroups] = useState<AlertGroup[] | null>(null)
  const [total, setTotal] = useState<number | null>(null)
  const [facets, setFacets] = useState<{ brands: { value: string; label: string }[]; productTypes: { value: string }[] } | null>(null)
  const [counts, setCounts] = useState<{ open: number; done: number; doneWeek: number } | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const generation = useRef(0)
  const morePending = useRef(false)
  const asOfRef = useRef<string | null>(null)
  const nextOffsetRef = useRef(0)
  const brandsKey = JSON.stringify(options.brands ?? [])
  const typesKey = JSON.stringify(options.productTypes ?? [])
  const queryKey = JSON.stringify([options.locationId, options.competitorId, options.reviewed, options.type, options.surface,
    options.limit, brandsKey, typesKey, options.sort, options.actionType, options.enabled, options.groupPrices])
  const currentKey = useRef(queryKey)
  // Invalidate old responses as soon as a render changes the view, before effects run.
  if (currentKey.current !== queryKey) { currentKey.current = queryKey; generation.current++ }

  const fetchAlerts = async (isStale?: () => boolean, append = false) => {
    if (options.enabled === false || currentKey.current !== queryKey) return
    if (!append) { generation.current++; asOfRef.current = null; nextOffsetRef.current = 0 }
    const requestGeneration = generation.current
    const stale = () => generation.current !== requestGeneration || isStale?.()
    append ? setLoadingMore(true) : setLoading(true)
    setError(null)
    if (!append) { setLoadingMore(false); setAlerts([]); setTotal(null); setCounts(null); setFacets(null); setGroups(null) }
    try {
      const params = new URLSearchParams()
      if (options.locationId) params.set('location_id', options.locationId)
      if (options.competitorId) params.set('competitor_id', options.competitorId)
      if (options.limit) params.set('limit', String(options.limit))
      if (append) {
        params.set('offset', String(groups ? nextOffsetRef.current : alerts.length))
        if (asOfRef.current) params.set('as_of', asOfRef.current)
      }
      if (options.type && options.type !== 'both') params.set('type', options.type)
      if (options.surface) params.set('surface', options.surface)
      if (options.brands?.length) params.set('brands', JSON.stringify(options.brands))
      if (options.productTypes?.length) params.set('product_types', JSON.stringify(options.productTypes))
      if (options.sort) params.set('sort', options.sort)
      if (options.actionType && options.actionType !== 'all') params.set('action_type', options.actionType)
      if (options.groupPrices) params.set('group_prices', 'true')
      if (options.includeFacets) params.set('facets', 'true')
      if (options.reviewed === 'all') params.set('reviewed', 'all')
      else if (options.reviewed === 'reviewed') params.set('reviewed', 'true')
      const res = await authFetch(`${API}/api/v1/alerts?${params}`)
      if (!res.ok) throw new Error('Failed to fetch alerts')
      const data = await res.json()
      if (stale()) return
      const groupedResponse = data.data?.pagination_unit === 'groups' && Array.isArray(data.data.groups)
      const rows: Alert[] = groupedResponse ? data.data.groups.flatMap((group: AlertGroup) => group.alerts) : data.data?.alerts || []
      setAlerts(previous => append ? [...previous, ...rows.filter(row => !previous.some(old => old.id === row.id))] : rows)
      if (groupedResponse) {
        asOfRef.current = data.data.as_of ?? null
        nextOffsetRef.current = Number(data.data.offset ?? 0) + data.data.groups.length
        const next: AlertGroup[] = data.data.groups
        setGroups(previous => append && previous ? [...previous, ...next.filter(group => !previous.some(old => old.id === group.id))] : next)
      } else if (!append) setGroups(null)
      setTotal(typeof data.data?.total === 'number' ? data.data.total : rows.length)
      setCounts(data.data?.counts ?? null)
      if (data.data?.facets) setFacets(data.data.facets)
    } catch (err: any) {
      if (!stale()) setError(append ? `More alerts could not be loaded. Try again.` : err.message)
    } finally {
      if (!stale()) { setLoading(false); setLoadingMore(false) }
    }
  }
  const loadMore = async () => {
    if (morePending.current || loading || total === null || (groups ? nextOffsetRef.current : alerts.length) >= total) return
    morePending.current = true
    try { await fetchAlerts(undefined, true) } finally { morePending.current = false }
  }

  const markReviewed = async (alertId: string) => {
    const res = await authFetch(`${API}/api/v1/alerts/${alertId}/reviewed`, { method: 'PATCH' })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error((body as any)?.error || `Failed to mark alert reviewed (HTTP ${res.status})`)
    }
    invalidateAlertCount()
    if (currentKey.current !== queryKey) return
    const wasOpen = alerts.some(alert => alert.id === alertId && !alert.reviewed)
    setAlerts((prev) => prev.map((a) => a.id === alertId ? { ...a, reviewed: true, reviewed_at: new Date().toISOString() } : a))
    if (wasOpen && !options.groupPrices) setCounts(previous => previous ? { open: Math.max(0, previous.open - 1), done: previous.done + 1, doneWeek: previous.doneWeek + 1 } : null)
    if (options.surface === 'today' && (options.reviewed !== 'all' || options.groupPrices)) await fetchAlerts()
  }

  const batchMarkReviewed = async (alertIds: string[]): Promise<BatchResult> => {
    const result = await performBatchMarkReviewed(alertIds, authFetch, API, invalidateAlertCount)
    if (result.successIds.length > 0) {
      const successSet = new Set(result.successIds)
      setAlerts((prev) => prev.map((a) => successSet.has(a.id) ? { ...a, reviewed: true } : a))
    }
    return result
  }

  // StrictMode-safe: the cleanup flag drops the first mount's resolution and
  // the shared authFetch GET cache collapses the duplicate network call.
  useEffect(() => {
    let cancelled = false
    fetchAlerts(() => cancelled)
    return () => { cancelled = true }
  }, [queryKey]) // eslint-disable-line react-hooks/exhaustive-deps

  return { alerts, groups, hasMore: total !== null && (groups ? nextOffsetRef.current : alerts.length) < total, loading, loadingMore, error, total, counts, facets, loadMore, markReviewed, batchMarkReviewed, refetch: fetchAlerts }
}

// ── Single alert (detail view, s13) ──────────────────────────────────────────
// Fetches one alert with its related context from GET /api/v1/alerts/:id. A 404
// (unknown id or out-of-tracking-scope) surfaces as notFound=true so the detail
// page can render an honest not-found state instead of a mock fallback.
export function useAlert(alertId: string | undefined) {
  const authFetch = useAuthFetch()
  const [alert, setAlert] = useState<Alert | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  const fetchAlert = async (isStale?: () => boolean) => {
    if (!alertId) return
    setLoading(true)
    setError(null)
    setNotFound(false)
    try {
      const res = await authFetch(`${API}/api/v1/alerts/${alertId}`)
      if (res.status === 404) {
        if (!isStale?.()) { setNotFound(true); setAlert(null) }
        return
      }
      if (!res.ok) throw new Error('Failed to load this alert')
      const data = await res.json()
      if (isStale?.()) return
      setAlert(data.data?.alert ?? null)
    } catch (err: any) {
      if (!isStale?.()) setError(err.message)
    } finally {
      if (!isStale?.()) setLoading(false)
    }
  }

  const markReviewed = async () => {
    if (!alertId) return
    const res = await authFetch(`${API}/api/v1/alerts/${alertId}/reviewed`, { method: 'PATCH' })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error((body as any)?.error || `Failed to mark alert reviewed (HTTP ${res.status})`)
    }
    setAlert((prev) => prev ? { ...prev, reviewed: true, reviewed_at: new Date().toISOString() } : prev)
    invalidateAlertCount()
  }

  useEffect(() => {
    let cancelled = false
    fetchAlert(() => cancelled)
    return () => { cancelled = true }
  }, [alertId]) // eslint-disable-line react-hooks/exhaustive-deps

  return { alert, loading, error, notFound, markReviewed, refetch: fetchAlert }
}
